/*
 * Controller for the DOM screens defined in index.html (spec sec. 9):
 * loading, intro (title + controls), pause, defeat, the lore overlays, and the
 * two capability-gate screens (mobile, unsupported).
 *
 * Per the user's decision there is no click-through start screen — the intro
 * card is a non-blocking overlay on the already-loaded world (see Game).
 *
 * Lore overlays are open-ended: any `#screen-lore-<id>` block is a valid target
 * for `showScreen("lore-<id>")`, so a new hologram-altar variant needs only its
 * HTML (see index.html / src/ui/lore.js) — no change here.
 */

const el = (id) => document.getElementById(id);

const screens = {
  loading: () => el("screen-loading"),
  intro: () => el("screen-intro"),
  mobile: () => el("screen-mobile"),
  unsupported: () => el("screen-unsupported"),
  pause: () => el("screen-pause"),
  defeat: () => el("screen-defeat"),
  scroll: () => el("screen-scroll"),
  portal: () => el("screen-portal"),
};

/** Every `#screen-lore-*` element currently in the document. */
function loreScreens() {
  return document.querySelectorAll('[id^="screen-lore-"]');
}

/** Hide every managed screen. */
export function hideAllScreens() {
  for (const get of Object.values(screens)) {
    const node = get();
    if (node) node.hidden = true;
  }
  for (const node of loreScreens()) node.hidden = true;
}

/**
 * Show exactly one screen and hide the rest. `name` is a key of `screens`, or
 * `"lore-<id>"` for a `#screen-lore-<id>` block.
 * @param {string} name
 */
export function showScreen(name) {
  hideAllScreens();
  const node = screens[name]?.() ?? el(`screen-${name}`);
  if (node) node.hidden = false;
}

/** Clone the shared control reference into the intro and pause screens. */
export function mountControls() {
  const tmpl = el("tmpl-controls");
  if (!(tmpl instanceof HTMLTemplateElement)) return;
  for (const id of ["intro-controls", "pause-controls"]) {
    const host = el(id);
    if (host && !host.childElementCount) {
      host.appendChild(tmpl.content.cloneNode(true));
    }
  }
}

/**
 * Update the loading screen.
 * @param {number} fraction 0..1
 * @param {string} [label]
 */
export function setLoadingProgress(fraction, label) {
  const fill = el("loading-bar-fill");
  if (fill) fill.style.width = `${Math.round(clamp01(fraction) * 100)}%`;
  const status = el("loading-status");
  if (status && label) status.textContent = label;
}

/** Cause line on the defeat screen (spec sec. 9). */
export function setDefeatReason(cause) {
  const node = el("screen-defeat-reason");
  if (!node) return;
  node.textContent =
    cause === "fall"
      ? "You fell from the mountain."
      : "The goblins got the better of you.";
}

/** Reason text on the unsupported-browser screen. */
export function setUnsupportedReason(text) {
  const node = el("screen-unsupported-reason");
  if (node && text) node.textContent = text;
}

function clamp01(v) {
  return v < 0 ? 0 : v > 1 ? 1 : v;
}
